import styled, { css } from "styled-components";
import { colors } from "../GlobalStyle/GlobalStyle";
import { workTypeDesignTokens } from "../work-type-shared/work-type-design-tokens";

const workTypeManagementDesignTokens = {
  ...workTypeDesignTokens,
  color: {
    canvas: colors.lightSkyBlue,
    surface: colors.white,
    surfaceMuted: colors.littleLightGrey,
    text: colors.midNightBlue,
    textMuted: colors.lightMidNightBlue,
    border: colors.frameGrey,
    borderStrong: colors.grey,
    focus: "#087F79"
  },
  focus: {
    ...workTypeDesignTokens.focus,
    outline: "3px solid #087F79"
  },
  tone: {
    ...workTypeDesignTokens.tone,
    approval: {
      background: "#EAF8F6",
      text: "#087F79",
      border: "#9ADFD6"
    },
    active: {
      background: "#F0FFFB",
      text: "#087F79",
      border: "#A8E6DD"
    },
    neutral: {
      background: colors.littleLightGrey,
      text: colors.lightMidNightBlue,
      border: colors.frameGrey
    },
    selected: {
      background: "#EAF8F6",
      text: colors.midNightBlue,
      border: "#9ADFD6"
    }
  }
} as const;

export const Screen = styled.main`
  --work-type-color-canvas: ${workTypeManagementDesignTokens.color.canvas};
  --work-type-color-surface: ${workTypeManagementDesignTokens.color.surface};
  --work-type-color-surface-muted: ${workTypeManagementDesignTokens.color.surfaceMuted};
  --work-type-color-text: ${workTypeManagementDesignTokens.color.text};
  --work-type-color-text-muted: ${workTypeManagementDesignTokens.color.textMuted};
  --work-type-color-border: ${workTypeManagementDesignTokens.color.border};
  --work-type-color-border-strong: ${workTypeManagementDesignTokens.color.borderStrong};
  --work-type-color-focus: ${workTypeManagementDesignTokens.color.focus};
  --work-type-tone-approval-background: ${workTypeManagementDesignTokens.tone.approval.background};
  --work-type-tone-approval-text: ${workTypeManagementDesignTokens.tone.approval.text};
  --work-type-tone-approval-border: ${workTypeManagementDesignTokens.tone.approval.border};
  --work-type-tone-active-background: ${workTypeManagementDesignTokens.tone.active.background};
  --work-type-tone-active-text: ${workTypeManagementDesignTokens.tone.active.text};
  --work-type-tone-active-border: ${workTypeManagementDesignTokens.tone.active.border};
  --work-type-tone-neutral-background: ${workTypeManagementDesignTokens.tone.neutral.background};
  --work-type-tone-neutral-text: ${workTypeManagementDesignTokens.tone.neutral.text};
  --work-type-tone-neutral-border: ${workTypeManagementDesignTokens.tone.neutral.border};
  --work-type-tone-selected-background: ${workTypeManagementDesignTokens.tone.selected.background};
  --work-type-tone-selected-text: ${workTypeManagementDesignTokens.tone.selected.text};
  --work-type-tone-selected-border: ${workTypeManagementDesignTokens.tone.selected.border};
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  flex: 1 1 auto;
  width: 100%;
  min-height: 100vh;
  min-width: 0;
  box-sizing: border-box;
  background: ${workTypeManagementDesignTokens.color.canvas};
  color: ${workTypeManagementDesignTokens.color.text};
  font-size: 14px;
`;

export const ScreenHeader = styled.header`
  position: sticky;
  top: 41px;
  z-index: 9;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${workTypeDesignTokens.spacing.md};
  padding: 20px 24px;
  border-bottom: 1px solid ${workTypeManagementDesignTokens.color.border};
  background: ${workTypeManagementDesignTokens.color.surface};

  h1 {
    margin: 0;
    color: ${workTypeManagementDesignTokens.color.text};
    font-size: 24px;
    line-height: 1.3;
  }

  p {
    margin: 4px 0 0;
    color: ${workTypeManagementDesignTokens.color.textMuted};
    line-height: 1.5;
  }
`;

export const MasterDetail = styled.div`
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  gap: ${workTypeDesignTokens.spacing.md};
  width: min(1760px, 100%);
  margin: 0 auto;
  padding: 16px 24px 32px;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 1380px) {
    grid-template-columns: 340px minmax(0, 1fr);
    padding-inline: 16px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Surface = styled.section`
  min-width: 0;
  border: 1px solid ${workTypeManagementDesignTokens.color.border};
  border-radius: ${workTypeDesignTokens.radius.dialog};
  background: ${workTypeManagementDesignTokens.color.surface};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
`;

export const CatalogSurface = styled(Surface)`
  position: sticky;
  top: ${workTypeDesignTokens.spacing.md};
  display: grid;
  grid-template-rows: auto auto minmax(280px, 1fr);
  align-self: start;
  max-height: calc(100vh - 196px);
  overflow: hidden;
`;

export const CatalogHeader = styled.header`
  display: grid;
  gap: ${workTypeDesignTokens.spacing.md};
  padding: ${workTypeDesignTokens.spacing.md};
  border-bottom: 1px solid ${workTypeManagementDesignTokens.color.border};
`;

export const CatalogTitleRow = styled.div`
  display: grid;
  gap: ${workTypeDesignTokens.spacing.xs};

  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${workTypeDesignTokens.spacing.sm};
  }

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: ${workTypeManagementDesignTokens.color.text};
    font-size: 18px;
    line-height: 1.35;
  }

  p {
    color: ${workTypeManagementDesignTokens.color.textMuted};
    font-size: 13px;
    line-height: 1.45;
  }

  strong {
    flex: 0 0 auto;
    padding: 4px 9px;
    border-radius: ${workTypeDesignTokens.radius.pill};
    background: ${workTypeManagementDesignTokens.color.surfaceMuted};
    color: ${workTypeManagementDesignTokens.color.textMuted};
    font-size: 12px;
  }
`;

export const TabRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  border-radius: ${workTypeDesignTokens.radius.control};
  background: ${workTypeManagementDesignTokens.color.surfaceMuted};
`;

export const TabButton = styled.button<{ active: boolean; tone: "approval" | "active" }>`
  min-height: ${workTypeDesignTokens.control.compactHeight};
  border: 1px solid transparent;
  border-radius: 8px;
  background: ${props => (props.active ? "white" : "transparent")};
  color: ${props =>
    props.active
      ? workTypeManagementDesignTokens.tone[props.tone].text
      : workTypeManagementDesignTokens.color.textMuted};
  font-weight: 700;
  cursor: pointer;
  ${props =>
    props.active &&
    css`
      border-color: ${workTypeManagementDesignTokens.tone[props.tone].border};
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
    `}

  &:focus-visible {
    outline: ${workTypeManagementDesignTokens.focus.outline};
    outline-offset: ${workTypeManagementDesignTokens.focus.offset};
  }
`;

export const FilterGrid = styled.div`
  display: grid;
  gap: ${workTypeDesignTokens.spacing.sm};

  input,
  select {
    width: 100%;
    min-width: 0;
    min-height: ${workTypeDesignTokens.control.compactHeight};
    border: 1px solid ${workTypeManagementDesignTokens.color.borderStrong};
    border-radius: ${workTypeDesignTokens.radius.control};
    background: white;
    color: ${workTypeManagementDesignTokens.color.text};
    box-sizing: border-box;
    font: inherit;
  }

  input {
    padding: 0 40px 0 12px;
  }

  input[type="search"]::-webkit-search-cancel-button {
    appearance: none;
  }

  select {
    padding: 0 10px;
  }

  input:focus-visible,
  select:focus-visible {
    outline: ${workTypeManagementDesignTokens.focus.outline};
    outline-offset: ${workTypeManagementDesignTokens.focus.offset};
  }
`;

export const SearchField = styled.div`
  position: relative;
`;

export const SearchClearButton = styled.button`
  position: absolute;
  top: 50%;
  right: 6px;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${workTypeManagementDesignTokens.color.textMuted};
  transform: translateY(-50%);
  cursor: pointer;

  &:hover {
    background: ${workTypeManagementDesignTokens.color.surfaceMuted};
  }

  &:focus-visible {
    outline: ${workTypeManagementDesignTokens.focus.outline};
    outline-offset: 0;
  }
`;

export const TypeFilterGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${workTypeDesignTokens.spacing.xs};
`;

export const TypeFilterButton = styled.button<{ active: boolean }>`
  min-height: ${workTypeDesignTokens.control.compactHeight};
  border: 1px solid
    ${props =>
      props.active
        ? workTypeManagementDesignTokens.color.focus
        : workTypeManagementDesignTokens.color.border};
  border-radius: ${workTypeDesignTokens.radius.pill};
  background: ${props =>
    props.active ? workTypeManagementDesignTokens.tone.selected.background : "white"};
  color: ${props =>
    props.active
      ? workTypeManagementDesignTokens.color.text
      : workTypeManagementDesignTokens.color.textMuted};
  font-weight: 700;
  cursor: pointer;

  &:focus-visible {
    outline: ${workTypeManagementDesignTokens.focus.outline};
    outline-offset: ${workTypeManagementDesignTokens.focus.offset};
  }
`;

export const CatalogMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px ${workTypeDesignTokens.spacing.md};
  border-bottom: 1px solid ${workTypeManagementDesignTokens.color.border};
  color: ${workTypeManagementDesignTokens.color.textMuted};

  > span {
    display: flex;
    align-items: center;
    gap: ${workTypeDesignTokens.spacing.sm};
  }
`;

export const CatalogActionButton = styled.button<{ primary?: boolean }>`
  min-height: ${workTypeDesignTokens.control.compactHeight};
  padding: 8px 12px;
  border: 1px solid
    ${props =>
      props.primary
        ? workTypeManagementDesignTokens.color.focus
        : workTypeManagementDesignTokens.color.borderStrong};
  border-radius: ${workTypeDesignTokens.radius.control};
  background: ${props =>
    props.primary ? workTypeManagementDesignTokens.color.focus : "white"};
  color: ${props => (props.primary ? "white" : workTypeManagementDesignTokens.color.text)};
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: ${workTypeManagementDesignTokens.focus.outline};
    outline-offset: ${workTypeManagementDesignTokens.focus.offset};
  }
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const PolicyList = styled.div`
  display: grid;
  align-content: start;
  gap: 8px;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;

  &:focus-visible {
    outline: ${workTypeManagementDesignTokens.focus.outline};
    outline-offset: -3px;
  }
`;

export const PolicyButton = styled.button<{ selected: boolean }>`
  width: 100%;
  padding: 0;
  border: 0;
  border-radius: ${workTypeDesignTokens.radius.card};
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${workTypeManagementDesignTokens.color.surfaceMuted};
  }

  &:focus-visible {
    outline: ${workTypeManagementDesignTokens.focus.outline};
    outline-offset: ${workTypeManagementDesignTokens.focus.offset};
  }
`;

export const StateMessage = styled.div`
  display: grid;
  place-items: center;
  min-height: 180px;
  padding: 24px;
  border: 1px dashed ${workTypeManagementDesignTokens.color.borderStrong};
  border-radius: ${workTypeDesignTokens.radius.card};
  margin: 12px;
  background: ${workTypeManagementDesignTokens.color.canvas};
  color: ${workTypeManagementDesignTokens.color.textMuted};
  text-align: center;
  line-height: 1.6;

  strong {
    color: ${workTypeManagementDesignTokens.color.text};
    font-size: 15px;
  }

  p {
    max-width: 270px;
    margin: 6px 0 0;
  }
`;

export const EditorSurface = styled(Surface)`
  align-self: start;
  min-width: 0;
  min-height: calc(100vh - 196px);

  > ${StateMessage} {
    box-sizing: border-box;
    min-height: 0;
    height: calc(100vh - 220px);
  }

  &[data-presentation="embedded-readonly"] {
    min-height: 0;

    > header {
      position: static;
    }

    input:disabled,
    select:disabled {
      opacity: 1;
      color: ${workTypeManagementDesignTokens.color.text};
      -webkit-text-fill-color: ${workTypeManagementDesignTokens.color.text};
      background: ${workTypeManagementDesignTokens.color.surfaceMuted};
      border-color: ${workTypeManagementDesignTokens.color.borderStrong};
    }
  }
`;

export const EditorHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${props => props.theme.colors.frameGrey};
  background: white;

  h2 {
    margin: 0;
    color: ${props => props.theme.colors.midNightBlue};
    font-size: 18px;
  }

  > div:first-child {
    display: grid;
    gap: 3px;
  }

  strong {
    color: ${workTypeManagementDesignTokens.color.text};
    font-size: 14px;
  }

  span {
    color: ${workTypeManagementDesignTokens.color.textMuted};
    font-size: 12px;
  }
`;

export const PolicyContextBanner = styled.div`
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: center;
  gap: ${workTypeDesignTokens.spacing.sm};
  padding: 14px 16px;
  border: 1px solid ${workTypeManagementDesignTokens.tone.approval.border};
  border-radius: ${workTypeDesignTokens.radius.card};
  background: ${workTypeManagementDesignTokens.tone.approval.background};

  strong { color: ${workTypeManagementDesignTokens.tone.approval.text}; }
  span { padding: 3px 8px; border-radius: ${workTypeDesignTokens.radius.pill}; background: white; color: ${workTypeManagementDesignTokens.color.textMuted}; font-size: 11px; }
  p { grid-column: 1 / -1; margin: 0; color: ${workTypeManagementDesignTokens.color.textMuted}; line-height: 1.5; }
`;

export const EditorBody = styled.div`
  display: grid;
  gap: 16px;
  padding: 18px 20px 24px;
`;

export const FormSection = styled.fieldset`
  display: grid;
  gap: 14px;
  min-width: 0;
  margin: 0;
  padding: 16px;
  border: 1px solid ${props => props.theme.colors.frameGrey};
  border-radius: 7px;

  legend {
    padding: 0 7px;
    color: ${props => props.theme.colors.midNightBlue};
    font-size: 14px;
    font-weight: 700;
  }
`;

export const GuidePanel = styled.aside`
  display: grid;
  gap: 10px;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid ${workTypeManagementDesignTokens.tone.approval.border};
  border-radius: ${workTypeDesignTokens.radius.card};
  background: ${workTypeManagementDesignTokens.tone.approval.background};
  color: ${workTypeManagementDesignTokens.color.text};
  font-size: 14px;
  line-height: 1.55;

  h3 { margin: 0; color: ${workTypeManagementDesignTokens.tone.approval.text}; font-size: 14px; }
  p, ul { margin: 0; }
  ul { padding-left: 20px; }
`;

export const GuideTextChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${workTypeDesignTokens.spacing.sm};

  span { padding: 6px 10px; border: 1px solid ${workTypeManagementDesignTokens.tone.approval.border}; border-radius: ${workTypeDesignTokens.radius.pill}; background: white; color: ${workTypeManagementDesignTokens.tone.approval.text}; font-weight: 700; }
`;

export const DisclosureBody = styled.div`
  padding: 14px;
  border-radius: ${workTypeDesignTokens.radius.card};
  background: ${workTypeManagementDesignTokens.color.surfaceMuted};
  color: ${workTypeManagementDesignTokens.color.textMuted};
  line-height: 1.55;

  ol { display: grid; gap: 9px; margin: 0; padding-left: 22px; }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 18px;
  min-width: 0;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.label`
  display: grid;
  align-content: start;
  gap: 5px;
  min-width: 0;
  color: ${props => props.theme.colors.midNightBlue};
  font-weight: 700;

  input,
  select {
    width: 100%;
    min-width: 0;
    min-height: 36px;
    box-sizing: border-box;
    font-weight: 400;
  }

  small {
    color: ${props => props.theme.colors.darkGrey};
    font-weight: 400;
    line-height: 1.45;
  }
`;

export const InlineChoice = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;

  label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

export const FlexibleModeHeading = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${workTypeDesignTokens.spacing.sm};

  strong {
    color: ${workTypeManagementDesignTokens.color.text};
    font-size: 15px;
  }
`;

export const FlexibleModeStatus = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 3px 10px;
  border: 1px solid ${workTypeManagementDesignTokens.tone.selected.border};
  border-radius: ${workTypeDesignTokens.radius.pill};
  background: ${workTypeManagementDesignTokens.tone.selected.background};
  color: ${workTypeManagementDesignTokens.tone.selected.text};
  font-size: 12px;
  font-weight: 700;
`;

export const FlexibleModeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${workTypeDesignTokens.spacing.sm};
  min-width: 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const FlexibleModeOption = styled.label<{ $checked: boolean }>`
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: start;
  gap: 10px;
  min-width: 0;
  min-height: 78px;
  box-sizing: border-box;
  padding: 14px;
  border: 2px solid
    ${props =>
      props.$checked
        ? workTypeManagementDesignTokens.color.focus
        : workTypeManagementDesignTokens.color.borderStrong};
  border-radius: ${workTypeDesignTokens.radius.card};
  background: ${props =>
    props.$checked ? workTypeManagementDesignTokens.tone.selected.background : "white"};
  color: ${workTypeManagementDesignTokens.color.text};
  cursor: pointer;

  &:focus-within {
    outline: ${workTypeManagementDesignTokens.focus.outline};
    outline-offset: ${workTypeManagementDesignTokens.focus.offset};
  }

  input {
    width: 18px;
    height: 18px;
    margin: 1px 0 0;
    accent-color: ${workTypeManagementDesignTokens.color.focus};
  }

  > span[aria-hidden="true"] {
    color: ${props =>
      props.$checked
        ? workTypeManagementDesignTokens.color.focus
        : workTypeManagementDesignTokens.color.textMuted};
    font-size: 17px;
    line-height: 1;
  }
`;

export const FlexibleModeCopy = styled.span`
  display: grid;
  gap: ${workTypeDesignTokens.spacing.xs};
  min-width: 0;

  strong {
    font-size: 14px;
  }

  small {
    color: ${workTypeManagementDesignTokens.color.textMuted};
    font-size: 13px;
    font-weight: 400;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }
`;

export const TargetOptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${workTypeDesignTokens.spacing.sm};

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

export const TargetOption = styled.label<{ $checked: boolean }>`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-height: ${workTypeDesignTokens.control.height};
  padding: 10px 12px;
  border: 1px solid
    ${props =>
      props.$checked
        ? workTypeManagementDesignTokens.color.focus
        : workTypeManagementDesignTokens.color.borderStrong};
  border-radius: ${workTypeDesignTokens.radius.card};
  background: ${props =>
    props.$checked ? workTypeManagementDesignTokens.tone.selected.background : "white"};
  color: ${workTypeManagementDesignTokens.color.text};
  font-weight: 700;
  cursor: pointer;

  &:focus-within {
    outline: ${workTypeManagementDesignTokens.focus.outline};
    outline-offset: ${workTypeManagementDesignTokens.focus.offset};
  }

  input {
    width: 18px;
    height: 18px;
    margin: 0;
    accent-color: ${workTypeManagementDesignTokens.color.focus};
  }
`;

export const TargetSelection = styled.div`
  display: grid;
  gap: ${workTypeDesignTokens.spacing.sm};
  padding: 14px;
  border: 1px solid ${workTypeManagementDesignTokens.tone.selected.border};
  border-radius: ${workTypeDesignTokens.radius.card};
  background: ${workTypeManagementDesignTokens.tone.selected.background};

  > p {
    margin: 0;
    color: ${workTypeManagementDesignTokens.color.textMuted};
    line-height: 1.5;
  }
`;

export const ErrorText = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.darkRed};
  font-weight: 600;
  line-height: 1.45;
`;

export const RuleList = styled.ol`
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;

  > li {
    display: grid;
    grid-template-columns: minmax(180px, 1fr) auto;
    gap: 10px;
    align-items: end;
    min-width: 0;
    padding: 10px;
    border: 1px solid ${props => props.theme.colors.frameGrey};
    border-radius: 5px;
  }

  @media (max-width: 1180px) {
    > li {
      grid-template-columns: 1fr;
    }
  }
`;

export const PreviewList = styled.ol`
  display: grid;
  gap: 6px;
  margin: 0;
  padding: 10px 10px 10px 30px;
  border-radius: 5px;
  background: ${props => props.theme.colors.littleLightGrey};
  color: ${props => props.theme.colors.darkGrey};
`;

export const ValidationSummary = styled.div`
  padding: 12px 14px;
  border-left: 4px solid ${props => props.theme.colors.darkRed};
  border-radius: 4px;
  background: #fff4f5;
  color: ${props => props.theme.colors.darkRed};

  strong,
  ul {
    display: block;
    margin: 0;
  }

  ul {
    padding: 6px 0 0 18px;
  }
`;

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;

export const ActionButton = styled.button<{ primary?: boolean; danger?: boolean }>`
  min-height: 36px;
  padding: 7px 14px;
  border: 1px solid
    ${props =>
      props.danger
        ? props.theme.colors.darkRed
        : props.primary
        ? props.theme.colors.veryDarkBlue
        : props.theme.colors.grey};
  border-radius: 4px;
  background: ${props =>
    props.danger
      ? "white"
      : props.primary
      ? props.theme.colors.veryDarkBlue
      : "white"};
  color: ${props =>
    props.danger
      ? props.theme.colors.darkRed
      : props.primary
      ? "white"
      : props.theme.colors.midNightBlue};
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 3px solid ${props => props.theme.colors.cobaltBlue};
    outline-offset: 2px;
  }
`;

export const RecoveryPanel = styled.section<{ danger?: boolean }>`
  display: grid;
  gap: 8px;
  padding: 14px;
  border: 1px solid
    ${props =>
      props.danger ? props.theme.colors.darkYellow : props.theme.colors.green};
  border-radius: 6px;
  background: ${props => (props.danger ? "#fff9eb" : "#f0fffb")};

  h3,
  p {
    margin: 0;
  }
`;
